import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { addQuery, parseQuery } from 'helpers/query';
import { isPublicWebsite } from 'helpers/html';
import { documentsClient } from 'clients/documents/documentsClient';

type UseSlugResult = {
	slug: string | null;
	loading: boolean;
	error: string | null;
};

export const useSlug = (): UseSlugResult => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => parseQuery(location.search), [location.search]);

  const slugFromQuery = useMemo(() => {
    const raw = query.slug;
    const v = Array.isArray(raw) ? raw[0] : raw;
    return typeof v === 'string' && v.trim() ? v : null;
  }, [query.slug]);

  const [resolvedSlug, setResolvedSlug] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slugFromQuery) return;
    if (!isPublicWebsite()) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const response = await documentsClient.getActivePublicSlug();
        const slug = response.data?.slug;

        if (!slug) {
          // eslint-disable-next-line
					throw new Error('Active slug not found');
        }

        if (cancelled) return;

        setResolvedSlug(slug);

        navigate(
          { search: addQuery(location.search, { slug }) },
          { replace: true },
        );
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'Failed to resolve slug');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slugFromQuery, navigate, location.search]);

  return {
    slug: slugFromQuery ?? resolvedSlug,
    loading,
    error,
  };
};